/**
 * 业务订单号 —— Y-01 的落地处:`SD` + `YYYYMMDD` + 6 位序列,与主键分开。
 *
 * ═══ 为什么单号和主键是两样东西 ═══
 *
 * 主键是 cuid,对外一律不暴露(AS-003)。对外只用这里生成的单号:
 * 客户在电话里念得出来、飞手在弱网下抄得下来、运营在 Excel 里搜得到。
 * 反过来,把主键暴露出去的代价是它变成了对外接口的一部分 ——
 * 以后想换主键策略(比如分库)就动不了了。
 *
 * ═══ 这个文件里为什么只有纯函数 ═══
 *
 * 生成一个单号要两样东西:今天是哪天(时钟)、今天已经有多少单(数据库)。
 * 两样都是 I/O,而 I/O 混进来之后这几条规则就只能靠起服务、造数据来验。
 * 所以这里只做「格式」:拼、解、算下一个序号。谁是今天、谁去查库,由调用方给
 * (apps/api/src/modules/order/order.service.ts)。
 *
 * ═══ 并发下的唯一性靠数据库,不靠这里 ═══
 *
 * order.orderNo 上有唯一索引。两个请求同时算出同一个序号时,
 * 第二个会在插入那一刻被数据库拒绝(P2002),调用方重试一次拿到新序号。
 * **不许**在应用层用「先查再插」的间隙当成安全的 —— 那个间隙就是重复单号的来源,
 * 而重复单号的表现是一个订单覆盖另一个订单的账。
 */

/** 单号前缀。写成常量是为了让「换前缀」是一次显式改动(它出现在契约的 OrderNo 参数正则里) */
export const ORDER_NO_PREFIX = 'SD'

/** 序列位数。6 位 = 每天最多 999999 单,够用几十年;超了必须报错,不许静默回绕 */
export const ORDER_NO_SEQ_DIGITS = 6

/** 与契约 components.parameters.OrderNo 的 pattern 等价:^SD[0-9]{8}[0-9]{6}$ */
export const ORDER_NO_PATTERN = /^SD(\d{8})(\d{6})$/

export class OrderNoError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'OrderNoError'
  }
}

/**
 * 单号里的「哪一天」按哪个时区算(Y-10)。**写死,不跟服务进程所在时区。**
 *
 * 这个常量存在的意义是让它成为一条**业务规则**而不是一个环境属性:
 * 部署环境的时区是运维配的,而单号里的日子是对账的依据 —— 两者不该是同一件事。
 */
export const ORDER_NO_TIMEZONE = 'Asia/Shanghai'

/**
 * 东八区相对 UTC 的偏移,分钟。
 *
 * 为什么是一个写死的偏移量而不是 `Intl.DateTimeFormat({ timeZone })`:
 * 中国大陆自 1991 年起不再实行夏令时,Asia/Shanghai 恒为 UTC+8,
 * 所以这个偏移在可预见的未来是精确的;而 Intl 依赖运行时的 ICU 数据 ——
 * 一个 small-icu 的 node 镜像上它会直接抛,那时最自然的「修法」就是退回本机时区,
 * 也就是 Y-10 要消灭的那件事。demo/s04-order-snapshot.mjs 里有一条断言
 * 拿 Intl 的 Asia/Shanghai 结果和这里的算术逐日比对,两者不一致会当场红。
 */
export const ORDER_NO_UTC_OFFSET_MINUTES = 480

/**
 * 日期部分 YYYYMMDD。**恒按东八区(Asia/Shanghai)取年月日**(Y-10)。
 *
 * ═══ 为什么不按服务进程所在时区取(那是 AS-011 的原做法,人已裁决改掉)═══
 *
 * 按进程时区取的话,容器 TZ 配成 UTC(镜像的默认值)时,
 * **每天 08:00 之前下的单会打上前一天的日期** —— 不报错、不告警、
 * 单号格式完全合法,只在对账时表现为「怎么少了一天的单」。
 * 而那时候手里只有一堆日期已经错了的单号,没有任何办法还原它们是哪天的。
 *
 * 于是「单号里的日子」就成了一个隐含依赖于部署环境的东西,
 * 而它是对账的唯一依据。写死之后它是一条业务规则:
 * 换机房、换镜像、换云厂商都不影响单号。
 *
 * 实现上只用 `getUTC*` + 一个固定偏移 —— **一个本机时区的 getter 都不许出现**,
 * 那是 guards/check-window-status-tz.mjs 静态断言的事。
 */
export function orderNoDatePart(now: Date): string {
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) {
    throw new OrderNoError(`拿到一个不合法的时间:${String(now)}`)
  }
  // 把 UTC 时刻平移到东八区,再用 UTC 的 getter 读出年月日 ——
  // 这样读出来的是「东八区的那一天」,与进程 TZ 无关
  const shifted = new Date(now.getTime() + ORDER_NO_UTC_OFFSET_MINUTES * 60_000)
  const y = shifted.getUTCFullYear()
  const m = shifted.getUTCMonth() + 1
  const d = shifted.getUTCDate()
  return `${pad(y, 4)}${pad(m, 2)}${pad(d, 2)}`
}

/** 拼单号。序号超出位数就抛 —— 回绕会撞上今天已经用过的号 */
export function buildOrderNo(datePart: string, seq: number): string {
  if (!/^\d{8}$/.test(datePart)) {
    throw new OrderNoError(`日期部分必须是 8 位数字(YYYYMMDD),拿到 ${JSON.stringify(datePart)}`)
  }
  if (!Number.isSafeInteger(seq) || seq < 1 || seq > maxSeq()) {
    throw new OrderNoError(
      `序号必须是 1 ~ ${maxSeq()} 的整数,拿到 ${JSON.stringify(seq)}。` +
        '今天的单量真的超了这个数,要加位数并同步改契约里 OrderNo 的正则 —— 不许回绕',
    )
  }
  return `${ORDER_NO_PREFIX}${datePart}${pad(seq, ORDER_NO_SEQ_DIGITS)}`
}

/** 解单号。**认不出来就返回 null,不做任何猜测** —— 猜出来的日期会进对账报表 */
export function parseOrderNo(orderNo: unknown): { datePart: string; seq: number } | null {
  if (typeof orderNo !== 'string') return null
  const m = ORDER_NO_PATTERN.exec(orderNo)
  if (!m) return null
  const datePart = m[1] as string
  const seq = Number(m[2])
  // 序号 0 不是合法单号:它意味着某处用了「从 0 开始数」,而人说的第一单是 1
  if (seq < 1) return null
  return { datePart, seq }
}

/**
 * 算今天的下一个序号。
 *
 * @param latestOrderNo 今天已有的最大单号(库里按 orderNo 倒序取第一条),没有就传 null
 * @param datePart 今天的日期部分
 *
 * 两种「不合预期」都抛,不猜:
 *   · 传进来的单号解不出来 → 库里有一行脏数据,继续算会撞号
 *   · 传进来的单号不是今天的 → 查询条件写错了(比如漏了前缀过滤),
 *     继续算会把昨天的序号接着往下用,而那正好会绕过唯一索引不报错
 */
export function nextOrderNoSeq(latestOrderNo: string | null, datePart: string): number {
  if (latestOrderNo === null) return 1
  const parsed = parseOrderNo(latestOrderNo)
  if (!parsed) {
    throw new OrderNoError(
      `库里今天最大的那个单号 ${JSON.stringify(latestOrderNo)} 不符合 SD+YYYYMMDD+6 位序列的格式。` +
        '这一行是脏数据 —— 先查清它是怎么进来的,别在它上面接着算序号',
    )
  }
  if (parsed.datePart !== datePart) {
    throw new OrderNoError(
      `拿到的最大单号是 ${parsed.datePart} 那天的,而现在要算 ${datePart} 的序号 —— ` +
        '查询条件没有按今天的前缀过滤。接着往下算会让今天的单号从一个奇怪的数字开始',
    )
  }
  return parsed.seq + 1
}

function maxSeq(): number {
  return Math.pow(10, ORDER_NO_SEQ_DIGITS) - 1
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0')
}
